import { BATTERY_BLACKOUT_CHARGE_FACTOR, ENGINE_FUEL_CHANGE_DURATION_TICKS, FUEL_BURN_SCALE_BY_DIFFICULTY, FUEL_PRODUCT_ID_BY_TYPE, FUEL_PROFILE_BY_TYPE } from '../../shared/constants.js';
import { t } from '../i18n.js';
import type { ControlInput, FuelType, GameSnapshot, Role } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import { createCard, createPlusMinusControl, getConsoleLineWidth, padDots } from '../controllerUiHelpers.js';

type CreateEngineRoomControllerOptions = {
  controls: HTMLDivElement | null;
  ctrlZoneLeftBottom: HTMLDivElement | null;
  ctrlZoneRightAux: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
};

type EngineHistoryRecord = {
  timestamp: number;
  /** Power produced by the engine. */
  engineMw: number;
  /** Power drawn from the battery to cover a shortfall. */
  batteryMw: number;
  /** Engine power nothing could use (battery already at its target, or blackout-throttled charging). */
  wastedMw: number;
  /** Engine power going into the battery. */
  chargeMw: number;
  /** Total power used by all rooms. */
  usedMw: number;
};

// Two-minute rolling window for the stacked power graph.
const ENGINE_HISTORY_WINDOW_MS = 120000;

// Short, human labels for each room that can draw continuous power.
const POWER_CONSUMER_LABELS: Partial<Record<Role, string>> = {
  pilot: 'Propulsion',
  ballast: 'Ballast',
  life_support: 'Life support',
  captain: 'Forward lights',
  docking: 'Docking lights',
  harpoon: 'Harpoon lights',
  periscope: 'Periscope light',
  storage: 'Processing',
  engineer: 'Bilge pumps',
  battery: 'Battery upkeep'
};

// Fixed stacking order + colours for the load bands and the pie chart, so a
// given consumer keeps the same colour across both.
const LOAD_ORDER: Role[] = ['pilot', 'ballast', 'life_support', 'storage', 'engineer', 'captain', 'docking', 'mining', 'periscope', 'battery'];
const LOAD_COLORS: Partial<Record<Role, string>> = {
  pilot: '#ff8a5b',
  ballast: '#ffd15b',
  life_support: '#5bd6ff',
  storage: '#ffb05b',
  engineer: '#5b8aff',
  captain: '#b48cff',
  docking: '#5bff9e',
  harpoon: '#ff5b8a',
  periscope: '#8affc0',
  battery: '#9aa26e'
};

/** Series colours for the power chart. */
const POWER_COLORS = {
  engine: '#5ee07a',
  battery: '#f0a43a',
  wasted: '#4aa3ff',
  used: '#ff6a5b'
} as const;

/**
 * Where the engine's power goes this tick. The battery is only drawn on when the engine
 * cannot cover demand, and only charged when below its target; whatever the engine makes
 * beyond that is wasted. Mirrors the server's battery step in RoomManager.
 */
const computePowerFlow = (sub: GameSnapshot['submarine']): Omit<EngineHistoryRecord, 'timestamp'> => {
  const engineMw = Math.max(0, sub.powerSupplyMw ?? 0);
  const usedMw = Math.max(0, sub.powerDemandMw ?? 0);
  const surplus = engineMw - usedMw;
  if (surplus < 0) {
    return { engineMw, batteryMw: -surplus, wastedMw: 0, chargeMw: 0, usedMw };
  }
  const target = Math.max(0, Math.min(100, sub.batteryChargeTarget ?? 100));
  const canCharge = sub.batteryCharge < target;
  // Blackout recovery throttles charging; the rest of the surplus is lost.
  const chargeMw = canCharge ? surplus * (sub.engineOverloaded ? BATTERY_BLACKOUT_CHARGE_FACTOR : 1) : 0;
  return { engineMw, batteryMw: 0, wastedMw: surplus - chargeMw, chargeMw, usedMw };
};

export const createEngineRoomController = ({
  controls,
  ctrlZoneLeftBottom,
  ctrlZoneRightAux,
  emitControl
}: CreateEngineRoomControllerOptions) => {
  let engineOutputTargetMw = 100;
  let engineOverloadLabel: HTMLElement | null = null;
  let engineResetButton: HTMLButtonElement | null = null;
  let engineResetProgress: HTMLElement | null = null;
  let enginePieLegend: HTMLElement | null = null;
  let engineHistoryCanvas: HTMLCanvasElement | null = null;
  let engineFuelButton: HTMLButtonElement | null = null;
  let loxBoostButton: HTMLButtonElement | null = null;
  let loxReadout: HTMLElement | null = null;

  // Fuel-change progress popup state.
  let fuelChangeOverlay: HTMLElement | null = null;
  let fuelChangeProgressFill: HTMLElement | null = null;
  let fuelChangeLabel: HTMLElement | null = null;
  let fuelChangeSeenActive = false;
  let fuelChangeFallbackTimer: number | null = null;

  const engineHistory: EngineHistoryRecord[] = [];

  const getFuelProfile = (fuelType: FuelType | undefined) => {
    const resolved = fuelType ?? 'soot';
    return FUEL_PROFILE_BY_TYPE[resolved] ?? FUEL_PROFILE_BY_TYPE.soot;
  };

  const getEngineFuelConsumptionPerMinute = (
    engineOutput: number,
    fuelType: FuelType | undefined,
    fuelCapacity: number,
    paused = false
  ): number => {
    if (engineOutput <= 0 || paused) {
      return 0;
    }
    const output = Math.max(1, Math.min(100, engineOutput));
    const fuelDrainTimeSeconds = 1800 - (output - 1) * (1500 / 99);
    const baseConsumption = 6000 / fuelDrainTimeSeconds;
    const profile = getFuelProfile(fuelType);
    const capacityMultiplier = 100 / Math.max(1, fuelCapacity);
    const difficultyScale = FUEL_BURN_SCALE_BY_DIFFICULTY[controllerState.lastRoomState?.difficulty ?? 'normal'] ?? 1;
    return baseConsumption * profile.burnRateMultiplier * difficultyScale * capacityMultiplier;
  };

  const formatFuelTypeLabel = (fuelType: FuelType | undefined): string => {
    return getFuelProfile(fuelType).label;
  };

  const formatEngineConsoleOutput = (snapshot: GameSnapshot): string => {
    const submarine = snapshot.submarine;
    const fuelTypeLabel = formatFuelTypeLabel(submarine.currentFuelType);
    const fuelProfile = getFuelProfile(submarine.currentFuelType);
    const fuelConsumption = getEngineFuelConsumptionPerMinute(
      submarine.engineOutput,
      submarine.currentFuelType,
      submarine.fuelCapacity,
      submarine.docked
    );
    const engineTemperatureC = submarine.insideTemperature;
    const supplyMw = submarine.powerSupplyMw ?? submarine.engineOutput * fuelProfile.powerOutputMultiplier;
    const demandMw = submarine.powerDemandMw ?? 0;
    const netMw = supplyMw - demandMw;
    const lw = getConsoleLineWidth();
    const sep = '='.repeat(lw);

    // Power consumers in a two-column grid (largest first, reading across then down).
    const columnGap = 2;
    const columnWidth = Math.floor((lw - columnGap) / 2);
    const consumerCells = (Object.keys(submarine.powerBreakdown ?? {}) as Role[])
      .map((role) => ({ role, mw: submarine.powerBreakdown?.[role] ?? 0 }))
      .filter((entry) => entry.mw > 0.05)
      .sort((a, b) => b.mw - a.mw)
      .map((entry) => padDots(`${POWER_CONSUMER_LABELS[entry.role] ?? entry.role}:`, `${entry.mw.toFixed(1)} MW`, columnWidth));
    const consumerLines: string[] = [];
    for (let i = 0; i < consumerCells.length; i += 2) {
      consumerLines.push(consumerCells[i] + (i + 1 < consumerCells.length ? ' '.repeat(columnGap) + consumerCells[i + 1] : ''));
    }

    // Time until the fuel runs out at the current output. The engine burns nothing while docked, at zero
    // output, or while swapping fuel, so there is no meaningful estimate then.
    const fuelRemainingMinutes = fuelConsumption > 0 ? Math.max(0, submarine.fuel) / fuelConsumption : null;
    const formatTimeUntilEmpty = (minutes: number | null): string => {
      if (minutes === null) return 'N/A';
      const totalSeconds = Math.round(minutes * 60);
      const hours = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      return hours > 0 ? `${hours}h ${String(mins).padStart(2, '0')}m` : `${mins}m ${String(secs).padStart(2, '0')}s`;
    };

    return [
      sep,
      padDots('Engine supply:', `${supplyMw.toFixed(1)} MW${submarine.loxBoostActive ? ' (LOX×2)' : ''}`, lw),
      padDots('Total demand:', `${demandMw.toFixed(1)} MW`, lw),
      padDots('Net to battery:', `${netMw >= 0 ? '+' : ''}${netMw.toFixed(1)} MW`, lw),
      padDots('Engine temperature:', `${engineTemperatureC.toFixed(1)} C`, lw),
      padDots('Fuel type:', fuelTypeLabel, lw),
      padDots('Fuel level:', `${Math.round(submarine.fuel)}%`, lw),
      padDots('Fuel consumption:', `${fuelConsumption.toFixed(1)}% / min`, lw),
      '',
      'Power consumers:',
      ...(consumerLines.length > 0 ? consumerLines : ['  (idle — no active draws)']),
      '',
      padDots('Time until empty:', formatTimeUntilEmpty(fuelRemainingMinutes), lw)
    ].join('\n');
  };


  const updateReadouts = (
    _engineOutput: number,
    _fuelPercent: number,
    _fuelType: FuelType | undefined,
    _fuelCapacity: number,
    _paused = false
  ): void => {
    // Labels removed — values are displayed via the console typewriter output.
  };

  const trimHistory = (): void => {
    const cutoff = Date.now() - ENGINE_HISTORY_WINDOW_MS;
    while (engineHistory.length > 0 && engineHistory[0].timestamp < cutoff) {
      engineHistory.shift();
    }
  };

  const recordHistoryPoint = (snapshot: GameSnapshot): void => {
    engineHistory.push({ timestamp: Date.now(), ...computePowerFlow(snapshot.submarine) });
    trimHistory();
  };

  const drawHistoryChart = (): void => {
    if (!engineHistoryCanvas) {
      return;
    }

    const canvas = engineHistoryCanvas;
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, canvas.clientWidth) * dpr;
    const height = Math.max(1, canvas.clientHeight) * dpr;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    context.setTransform(dpr, 0, 0, dpr, 0, 0);

    const W = width / dpr;
    const H = height / dpr;
    context.clearRect(0, 0, W, H);
    context.fillStyle = '#06121a';
    context.fillRect(0, 0, W, H);

    const leftPad = 40;
    const topPad = 8;
    const bottomPad = 16;
    const plotLeft = leftPad;
    const plotRight = W - 6;
    const usable = H - topPad - bottomPad;
    const posHeight = usable * 0.55; // power available (engine + battery)
    const negHeight = usable * 0.45; // power spent (rooms + battery charging)
    const zeroY = topPad + posHeight;

    const now = Date.now();
    const windowStart = now - ENGINE_HISTORY_WINDOW_MS;
    const points = engineHistory.filter((record) => record.timestamp >= windowStart);

    // Round the scale up so the gridlines sit on tidy numbers.
    const niceMax = (value: number, floor: number): number => {
      const raw = Math.max(floor, value);
      const step = raw > 200 ? 100 : raw > 80 ? 50 : 25;
      return Math.ceil(raw / step) * step;
    };
    const maxAbove = niceMax(Math.max(0, ...points.map((r) => r.engineMw + r.batteryMw)), 50);
    const maxBelow = niceMax(Math.max(0, ...points.map((r) => r.usedMw + r.chargeMw)), 25);

    const xFor = (ts: number): number => plotLeft + ((ts - windowStart) / ENGINE_HISTORY_WINDOW_MS) * (plotRight - plotLeft);
    const yUp = (v: number): number => zeroY - Math.min(1, Math.max(0, v / maxAbove)) * posHeight;
    const yDown = (v: number): number => zeroY + Math.min(1, Math.max(0, v / maxBelow)) * negHeight;

    // Faint tint either side of the zero line.
    context.fillStyle = 'rgba(94, 224, 122, 0.06)';
    context.fillRect(plotLeft, topPad, plotRight - plotLeft, zeroY - topPad);
    context.fillStyle = 'rgba(255, 106, 91, 0.06)';
    context.fillRect(plotLeft, zeroY, plotRight - plotLeft, topPad + usable - zeroY);

    // Gridlines with MW labels.
    context.textBaseline = 'middle';
    const grid = (y: number, label: string, strong = false): void => {
      context.strokeStyle = strong ? 'rgba(220, 235, 245, 0.5)' : 'rgba(200, 220, 235, 0.12)';
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(plotLeft, y + 0.5);
      context.lineTo(plotRight, y + 0.5);
      context.stroke();
      context.fillStyle = 'rgba(190, 215, 230, 0.8)';
      context.font = '9px monospace';
      context.textAlign = 'right';
      context.fillText(label, plotLeft - 4, y);
    };
    grid(topPad, `${maxAbove}`);
    grid(zeroY - posHeight / 2, `${Math.round(maxAbove / 2)}`);
    grid(zeroY, '0 MW', true);
    grid(zeroY + negHeight / 2, `-${Math.round(maxBelow / 2)}`);
    grid(topPad + usable, `-${maxBelow}`);

    if (points.length > 0) {
      const band = (
        lower: (r: EngineHistoryRecord) => number,
        upper: (r: EngineHistoryRecord) => number,
        mapY: (v: number) => number,
        fill: string
      ): void => {
        context.beginPath();
        points.forEach((r, i) => {
          const x = xFor(r.timestamp);
          const y = mapY(upper(r));
          if (i === 0) context.moveTo(x, y); else context.lineTo(x, y);
        });
        for (let i = points.length - 1; i >= 0; i -= 1) {
          context.lineTo(xFor(points[i].timestamp), mapY(lower(points[i])));
        }
        context.closePath();
        context.fillStyle = fill;
        context.fill();
      };

      // Above the line: engine output (its unused part highlighted as waste), then battery draw on top.
      band(() => 0, (r) => r.engineMw - r.wastedMw, yUp, `${POWER_COLORS.engine}cc`);
      band((r) => r.engineMw - r.wastedMw, (r) => r.engineMw, yUp, `${POWER_COLORS.wasted}d9`);
      band((r) => r.engineMw, (r) => r.engineMw + r.batteryMw, yUp, `${POWER_COLORS.battery}d9`);

      // Below the line: everything the rooms use, then power going into the battery.
      band(() => 0, (r) => r.usedMw, yDown, `${POWER_COLORS.used}cc`);
      band((r) => r.usedMw, (r) => r.usedMw + r.chargeMw, yDown, `${POWER_COLORS.battery}d9`);
    }

    // Emphasised zero baseline.
    context.strokeStyle = 'rgba(230, 240, 250, 0.6)';
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(plotLeft, zeroY + 0.5);
    context.lineTo(plotRight, zeroY + 0.5);
    context.stroke();

    // X-axis time labels (fixed 2-minute window).
    context.fillStyle = 'rgba(190, 215, 230, 0.85)';
    context.font = '9px monospace';
    context.textBaseline = 'top';
    context.textAlign = 'left';
    context.fillText('-2 min', plotLeft, topPad + usable + 3);
    context.textAlign = 'center';
    context.fillText('-1 min', (plotLeft + plotRight) / 2, topPad + usable + 3);
    context.textAlign = 'right';
    context.fillText('now', plotRight, topPad + usable + 3);
  };

  // Populate the 2-column drains legend (replaces the old pie chart).
  const updateDrainsLegend = (snapshot: GameSnapshot): void => {
    if (!enginePieLegend) {
      return;
    }
    const breakdown = snapshot.submarine.powerBreakdown ?? {};
    const entries = LOAD_ORDER
      .map((role) => ({ role, mw: breakdown[role] ?? 0 }))
      .filter((e) => e.mw > 0.05)
      .sort((a, b) => b.mw - a.mw);
    const total = entries.reduce((s, e) => s + e.mw, 0);
    if (total <= 0) {
      enginePieLegend.innerHTML = '<span class="engine-pie-legend-item engine-pie-legend-empty">No active load</span>';
      return;
    }
    enginePieLegend.innerHTML = entries.map((e) => {
      const pct = ((e.mw / total) * 100).toFixed(0);
      const color = LOAD_COLORS[e.role] ?? '#888888';
      const label = POWER_CONSUMER_LABELS[e.role] ?? e.role;
      return `<span class="engine-pie-legend-item"><span class="engine-pie-swatch" style="background:${color}"></span><span class="engine-pie-legend-text">${label} · ${e.mw.toFixed(1)} MW (${pct}%)</span></span>`;
    }).join('');
  };

  const getStoredFuelCount = (fuelType: FuelType): number => {
    const roomState = controllerState.lastRoomState;
    if (!roomState) {
      return 0;
    }
    const fuelProductId = FUEL_PRODUCT_ID_BY_TYPE[fuelType];
    return roomState.cargo[fuelProductId] ?? 0;
  };

  const getQueuedFuelCount = (fuelType: FuelType): number => {
    const roomState = controllerState.lastRoomState;
    if (!roomState) {
      return 0;
    }
    const fuelProductId = FUEL_PRODUCT_ID_BY_TYPE[fuelType];
    // Each craft job produces one unit of its item.
    return roomState.craftQueue.reduce((sum, job) => sum + (job.id === fuelProductId ? 1 : 0), 0);
  };

  const openFuelDialog = (): void => {
    const roomState = controllerState.lastRoomState;
    if (!roomState) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'engine-fuel-dialog-overlay';

    const dialog = document.createElement('div');
    dialog.className = 'engine-fuel-dialog';

    const heading = document.createElement('h3');
    heading.textContent = t('change_fuel_heading');

    const subheading = document.createElement('p');
    subheading.textContent = t('select_fuel');

    const list = document.createElement('div');
    list.className = 'engine-fuel-dialog-list';

    const fuelTypes: FuelType[] = ['soot', 'refined', 'iridium_carbon'];
    for (const fuelType of fuelTypes) {
      const profile = getFuelProfile(fuelType);
      const stored = getStoredFuelCount(fuelType);
      const queued = getQueuedFuelCount(fuelType);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'engine-fuel-choice';
      button.disabled = stored <= 0;
      button.innerHTML = `<strong>${profile.label}</strong><span>Stored: ${stored} | Processing queue: ${queued}</span>`;
      button.addEventListener('click', () => {
        emitControl({ action: 'engine_change_fuel', fuelType });
        overlay.remove();
        openFuelChangeProgress(profile.label);
      });
      list.appendChild(button);
    }

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'secondary';
    closeButton.textContent = t('close_btn');
    closeButton.addEventListener('click', () => overlay.remove());

    dialog.append(heading, subheading, list, closeButton);
    overlay.appendChild(dialog);

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        overlay.remove();
      }
    });

    document.body.appendChild(overlay);
  };

  const closeFuelChangeProgress = (): void => {
    if (fuelChangeFallbackTimer !== null) {
      window.clearTimeout(fuelChangeFallbackTimer);
      fuelChangeFallbackTimer = null;
    }
    fuelChangeOverlay?.remove();
    fuelChangeOverlay = null;
    fuelChangeProgressFill = null;
    fuelChangeLabel = null;
    fuelChangeSeenActive = false;
  };

  const openFuelChangeProgress = (fuelLabel: string): void => {
    // Replace any existing popup so a fresh change always starts clean.
    closeFuelChangeProgress();

    const overlay = document.createElement('div');
    overlay.className = 'engine-fuel-dialog-overlay';

    const dialog = document.createElement('div');
    dialog.className = 'engine-fuel-dialog engine-fuel-progress-dialog';

    const heading = document.createElement('h3');
    heading.textContent = t('changing_fuel');

    const label = document.createElement('p');
    label.textContent = `Loading ${fuelLabel}… engine running on battery.`;
    fuelChangeLabel = label;

    const progressWrap = document.createElement('div');
    progressWrap.className = 'engine-reset-progress';
    const progressFill = document.createElement('div');
    progressFill.className = 'engine-reset-progress-fill';
    progressFill.style.width = '0%';
    progressWrap.appendChild(progressFill);
    fuelChangeProgressFill = progressFill;

    dialog.append(heading, label, progressWrap);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    fuelChangeOverlay = overlay;
    fuelChangeSeenActive = false;

    // Safety net: if the change never registers server-side (e.g. the canister
    // was consumed elsewhere), don't leave the popup stuck open.
    fuelChangeFallbackTimer = window.setTimeout(() => {
      if (!fuelChangeSeenActive) {
        closeFuelChangeProgress();
      }
    }, 2000);
  };

  const updateFuelChangeProgress = (snapshot: GameSnapshot): void => {
    const remaining = snapshot.submarine.fuelChangeTicksRemaining;

    if (remaining > 0) {
      // A change is underway (started here or already in progress on connect).
      if (!fuelChangeOverlay) {
        const pendingType = snapshot.submarine.fuelChangePendingType;
        openFuelChangeProgress(getFuelProfile(pendingType ?? undefined).label);
      }
      fuelChangeSeenActive = true;
      if (fuelChangeProgressFill) {
        const elapsed = ENGINE_FUEL_CHANGE_DURATION_TICKS - remaining;
        const percent = Math.max(0, Math.min(100, (elapsed / ENGINE_FUEL_CHANGE_DURATION_TICKS) * 100));
        fuelChangeProgressFill.style.width = `${percent}%`;
      }
      return;
    }

    // remaining <= 0: close once we've actually observed the change running.
    if (fuelChangeOverlay && fuelChangeSeenActive) {
      if (fuelChangeProgressFill) {
        fuelChangeProgressFill.style.width = '100%';
      }
      if (fuelChangeLabel) {
        fuelChangeLabel.textContent = t('fuel_loaded');
      }
      window.setTimeout(() => closeFuelChangeProgress(), 350);
      fuelChangeSeenActive = false;
    }
  };

  const updateUi = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) {
      return;
    }

    if (engineOverloadLabel) {
      if (snapshot.submarine.engineNoFuel) {
        engineOverloadLabel.textContent = t('no_fuel_stopped');
        engineOverloadLabel.style.animation = 'engineAlertFlash 1s ease-in-out infinite';
      } else if (snapshot.submarine.engineOverloaded) {
        engineOverloadLabel.textContent = snapshot.submarine.engineResetTicksRemaining > 0 ? 'OVERLOAD — RESETTING' : 'OVERLOAD';
        engineOverloadLabel.style.animation = 'engineAlertFlash 1s ease-in-out infinite';
      } else {
        engineOverloadLabel.textContent = '';
        engineOverloadLabel.style.animation = 'none';
      }
    }
    if (engineResetButton) {
      if (snapshot.submarine.engineNoFuel) {
        const hasFuel = snapshot.submarine.fuel > 0;
        engineResetButton.disabled = !hasFuel;
        engineResetButton.textContent = hasFuel ? 'Restart engine' : 'Load fuel first';
      } else if (snapshot.submarine.engineOverloaded) {
        const canResetEngine = snapshot.submarine.engineResetTicksRemaining <= 0 && snapshot.submarine.batteryCharge >= 5;
        engineResetButton.disabled = !canResetEngine;
        engineResetButton.textContent = snapshot.submarine.engineResetTicksRemaining > 0
          ? 'Resetting...'
          : canResetEngine
            ? 'Reset engine'
            : 'Charging battery to 5%…';
      } else {
        engineResetButton.disabled = true;
        engineResetButton.textContent = t('reset_engine');
      }
    }
    if (engineResetProgress) {
      const progress = snapshot.submarine.engineResetTicksRemaining > 0 ? Math.round((150 - snapshot.submarine.engineResetTicksRemaining) / 150 * 100) : 0;
      engineResetProgress.style.width = `${progress}%`;
    }

    if (engineFuelButton) {
      engineFuelButton.disabled = snapshot.submarine.fuelChangeTicksRemaining > 0;
    }

    const lox = snapshot.submarine.liquidOxygen ?? 0;
    const boosting = snapshot.submarine.loxBoostActive ?? false;
    if (loxReadout) {
      loxReadout.textContent = `Liquid O₂: ${Math.round(lox)}%`;
    }
    if (loxBoostButton) {
      loxBoostButton.disabled = !boosting && lox <= 0;
      loxBoostButton.textContent = boosting ? 'LOX boost: ON (×2)' : 'LOX boost: OFF';
      loxBoostButton.classList.toggle('is-active', boosting);
    }

    updateFuelChangeProgress(snapshot);
    updateDrainsLegend(snapshot);

    engineOutputTargetMw = Math.round(snapshot.submarine.engineOutput);
    updateReadouts(
      snapshot.submarine.engineOutput,
      snapshot.submarine.fuel,
      snapshot.submarine.currentFuelType,
      snapshot.submarine.fuelCapacity,
      snapshot.submarine.docked
    );

    if (engineHistoryCanvas) {
      recordHistoryPoint(snapshot);
      drawHistoryChart();
    }
  };

  const render = (): void => {
    if (!controls) {
      return;
    }
    controls.classList.add('engine-controls-layout');

    const consoleCard = createCard('Engine Room', 'Realtime propulsion diagnostics and fuel telemetry.');
    consoleCard.classList.add('ctrl-room-main-card', 'room-console-card');

    const overloadLabel = document.createElement('div');
    overloadLabel.className = 'engine-console-alert';
    consoleCard.append(overloadLabel);
    engineOverloadLabel = overloadLabel;

    const engineChartWrap = document.createElement('div');
    engineChartWrap.className = 'engine-history-chart engine-history-chart-tall';
    const engineChartCanvas = document.createElement('canvas');
    engineChartCanvas.className = 'engine-history-canvas';
    engineChartCanvas.setAttribute('aria-hidden', 'true');
    engineChartWrap.append(engineChartCanvas);
    consoleCard.append(engineChartWrap);
    engineHistoryCanvas = engineChartCanvas;
    window.requestAnimationFrame(() => drawHistoryChart());

    // Power drains legend (two columns), below the history graph.
    const pieWrap = document.createElement('div');
    pieWrap.className = 'engine-pie-wrap';
    const pieTitle = document.createElement('div');
    pieTitle.className = 'engine-pie-title';
    pieTitle.textContent = t('current_power_drains');
    const pieLegend = document.createElement('div');
    pieLegend.className = 'engine-pie-legend engine-pie-legend-grid';
    pieWrap.append(pieTitle, pieLegend);
    consoleCard.append(pieWrap);
    enginePieLegend = pieLegend;

    controls.append(consoleCard);

    const leftPanel = createCard('Engine Actions', 'Emergency controls and fuel canister loading.');
    leftPanel.classList.add('ctrl-room-aux-card', 'engine-zone-card');

    const resetButton = document.createElement('button');
    resetButton.type = 'button';
    resetButton.className = 'primary';
    resetButton.textContent = t('reset_engine');
    resetButton.addEventListener('click', () => emitControl({ action: 'engine_reset' }));
    engineResetButton = resetButton;

    const fuelButton = document.createElement('button');
    fuelButton.type = 'button';
    fuelButton.className = 'secondary';
    fuelButton.textContent = t('change_fuel');
    fuelButton.addEventListener('click', () => openFuelDialog());
    engineFuelButton = fuelButton;

    const resetProgressWrap = document.createElement('div');
    resetProgressWrap.className = 'engine-reset-progress';
    const resetProgress = document.createElement('div');
    resetProgress.className = 'engine-reset-progress-fill';
    resetProgressWrap.appendChild(resetProgress);
    engineResetProgress = resetProgress;

    const loxLabel = document.createElement('div');
    loxLabel.className = 'engine-lox-readout';
    loxLabel.textContent = 'Liquid O₂: 0%';
    loxReadout = loxLabel;

    if (controllerState.lastRoomState?.ownedUpgrades.includes('unlock-lox-boost')) {
      const loxButton = document.createElement('button');
      loxButton.type = 'button';
      loxButton.className = 'secondary engine-lox-boost-button';
      loxButton.textContent = 'LOX boost: OFF';
      loxButton.addEventListener('click', () => {
        const active = controllerState.lastSnapshot?.submarine.loxBoostActive ?? false;
        emitControl({ action: 'engine_lox_boost', active: !active });
      });
      loxBoostButton = loxButton;
    }

    leftPanel.append(resetButton, fuelButton, resetProgressWrap, loxLabel, ...(loxBoostButton ? [loxBoostButton] : []));
    if (ctrlZoneLeftBottom) {
      ctrlZoneLeftBottom.append(leftPanel);
    }

    const rightPanel = document.createElement('div');
    rightPanel.className = 'control-card ctrl-room-aux-card engine-zone-card';

    const stepOutput = (delta: number): void => {
      const snapshot = controllerState.lastSnapshot;
      if (!snapshot) {
        return;
      }
      if (snapshot.submarine.engineOverloaded || snapshot.submarine.engineResetTicksRemaining > 0) {
        return;
      }

      engineOutputTargetMw = Math.max(0, Math.min(100, engineOutputTargetMw + delta));
      emitControl({ action: 'engine_set_output', value: engineOutputTargetMw });
    };

    const engineOutputControl = createPlusMinusControl(
      'Engine output',
      {
        title: 'Decrease engine output',
        ariaLabel: 'Decrease engine output',
        onStep: () => stepOutput(-5),
        holdRepeatOptions: { initialDelayMs: 260, repeatDelayMs: 95 }
      },
      {
        title: 'Increase engine output',
        ariaLabel: 'Increase engine output',
        onStep: () => stepOutput(5),
        holdRepeatOptions: { initialDelayMs: 260, repeatDelayMs: 95 }
      },
      { buttonRowClassName: 'engine-output-button-row' }
    );

    rightPanel.append(engineOutputControl.wrap);
    if (ctrlZoneRightAux) {
      ctrlZoneRightAux.append(rightPanel);
    }

    updateUi(controllerState.lastSnapshot);
  };

  return {
    render,
    updateUi
  };
};
